import { useRef, useState, useCallback, useMemo } from 'react';

import {
  useButtonPressedCallback,
  useClickstreamCallback
} from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import IconButton from '@zenbusiness/zui/IconButton';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import PropTypes from 'prop-types';

import { ActionTwoButtonsGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import Steps from '#client/components/Steps';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useScrollTo from '#client/hooks/useScrollTo';
import useToastContext from '#client/hooks/useToastContext';

import { DialogContentStyled, DialogStyled, DialogTitleStyled } from './StepsModal.styles';

import ActionButtonsGrid from '../Grid/ActionButtonsGrid';

export const StepsModal = ({
  onClose = () => {},
  steps,
  title,
  disableBack = false,
  disableClose = false,
  isHideHeader = false
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const contentRef = useRef(null);
  const paperRef = useRef(null);
  const { clearAllToast } = useToastContext();
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const totalSteps = () => steps.length;
  const isLastStep = () => activeStep === totalSteps() - 1;

  useScrollTo(0, 0, contentRef, [activeStep]);
  useScrollTo(0, 0, paperRef, [activeStep]);

  const handleNext = () => {
    if (steps[activeStep]?.NextButton?.OnClick) {
      // eslint-disable-next-line no-undef
      steps[activeStep].NextButton.OnClick(...arguments);
    }
    if (isLastStep()) {
      return;
    }

    setActiveStep(activeStep + 1);
  };

  const trackCancelClick = useButtonPressedCallback({
    elementName: steps[activeStep]?.TrackingEvent,
    elementText: 'Exit',
    appPageName: steps[activeStep]?.TrackingEvent
  });
  const handleCancel = () => {
    if (steps[activeStep]?.CancelButton?.OnClick) {
      // eslint-disable-next-line no-undef
      steps[activeStep].CancelButton.OnClick(...arguments);
    }
    // eslint-disable-next-line no-undef
    onClose(...arguments);
    trackCancelClick();
  };

  const trackClearToastClick = useButtonPressedCallback({
    elementName: steps[activeStep]?.TrackingEvent,
    elementText: 'Exit',
    appPageName: steps[activeStep]?.TrackingEvent
  });
  const handleClearToastAndClose = useCallback(() => {
    clearAllToast();
    onClose();
    trackClearToastClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearAllToast, onClose, steps, activeStep]);

  const trackBackClick = useClickstreamCallback('buttonPressed');
  const handleBack = () => {
    if (activeStep === 0) {
      onClose();
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    trackBackClick({
      elementName: steps[activeStep]?.TrackingEvent,
      elementText: 'Back',
      appPageName: steps[activeStep]?.TrackingEvent
    });
  };

  const GroupButton = useMemo(() => {
    // When modal has no button
    if (!steps[activeStep]?.CancelButton && !steps[activeStep]?.NextButton) {
      return null;
    }

    // When modal has 1 button
    if (
      (steps[activeStep]?.CancelButton && !steps[activeStep]?.NextButton) ||
      (!steps[activeStep]?.CancelButton && steps[activeStep]?.NextButton)
    ) {
      const sx = {
        position: 'initial !important',
        backgroundColor: 'transparent',
        backdropFilter: 'none',
        marginTop: isDesktop ? '-56px' : '-26px'
      };

      return (
        <ActionButtonsGrid
          sx={steps[activeStep]?.isInlineButton ? sx : {}}
          type={steps[activeStep]?.actionButtonType}
        >
          {steps[activeStep]?.CancelButton && (
            <LoadingButton
              disabled={steps[activeStep]?.CancelButton?.Disabled}
              fullWidth={!isDesktop}
              onClick={handleCancel}
              size="large"
            >
              {steps[activeStep]?.CancelButton?.Text || 'Cancel'}
            </LoadingButton>
          )}
          {steps[activeStep]?.NextButton && (
            <LoadingButton
              aria-label="Advance to next step"
              color="primary"
              disabled={steps[activeStep]?.NextButton?.Disabled}
              fullWidth={!isDesktop}
              loading={steps[activeStep]?.NextButton?.Loading}
              onClick={handleNext}
              size="large"
              variant="contained"
            >
              {steps[activeStep]?.NextButton?.Text || 'Next'}
            </LoadingButton>
          )}
        </ActionButtonsGrid>
      );
    }
    // When modal has 2 buttons
    return (
      <ActionTwoButtonsGrid
        firstButton={
          <LoadingButton
            disabled={steps[activeStep]?.CancelButton?.Disabled}
            onClick={handleCancel}
          >
            {steps[activeStep]?.CancelButton?.Text || 'Cancel'}
          </LoadingButton>
        }
        secondButton={
          <LoadingButton
            aria-label="Advance to next step"
            color="primary"
            disabled={steps[activeStep]?.NextButton?.Disabled}
            loading={steps[activeStep]?.NextButton?.Loading}
            onClick={handleNext}
            variant="contained"
          >
            {steps[activeStep]?.NextButton?.Text || 'Next'}
          </LoadingButton>
        }
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, steps]);

  return (
    <DialogStyled
      disableRestoreFocus
      fullScreen
      onClose={onClose}
      open
      PaperProps={{ ref: paperRef, id: 'scroll_page' }}
    >
      {!isHideHeader && (
        <>
          <DialogTitleStyled component="div">
            {disableBack ? (
              <div />
            ) : (
              <IconButton
                label="Go back to previous step"
                onClick={handleBack}
              >
                <SharedAsset
                  alt=""
                  src="/icon/micro/caret-left.svg"
                />
              </IconButton>
            )}
            <Box>{title}</Box>
            {disableClose ? (
              <div />
            ) : (
              <IconButton
                label="Close steps modal"
                onClick={handleClearToastAndClose}
              >
                <SharedAsset
                  alt=""
                  src="/icon/micro/close-x.svg"
                />
              </IconButton>
            )}
          </DialogTitleStyled>
          <Steps
            currentStepIndex={activeStep}
            stepNames={steps.map((_, i) => i)}
          />
        </>
      )}
      <DialogContentStyled
        isInlineButton={steps[activeStep]?.isInlineButton}
        ref={contentRef}
        showScroll={steps[activeStep]?.showScroll}
      >
        {steps[activeStep]?.StepContent}
        {steps[activeStep]?.isInlineButton && GroupButton}
      </DialogContentStyled>
      {!steps[activeStep]?.isInlineButton && GroupButton}
    </DialogStyled>
  );
};

StepsModal.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      StepContent: PropTypes.node,
      showScroll: PropTypes.bool,
      NextButton: PropTypes.shape({
        Disabled: PropTypes.bool,
        Loading: PropTypes.bool,
        Text: PropTypes.string,
        OnClick: PropTypes.func
      }),
      CancelButton: PropTypes.shape({
        Disabled: PropTypes.bool,
        Text: PropTypes.string,
        OnClick: PropTypes.func
      }),
      TrackingEvent: PropTypes.shape({}),
      actionButtonType: PropTypes.oneOf(Object.values(ACTION_BUTTON_TYPE)),
      isInlineButton: PropTypes.bool
    })
  ),
  title: PropTypes.node.isRequired,
  onClose: PropTypes.func,
  disableBack: PropTypes.bool,
  disableClose: PropTypes.bool,
  isHideHeader: PropTypes.bool
};
